import { useState, type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, ShoppingBag, Receipt, CalendarDays, Download,
  Menu, X, LogOut, BarChart3, Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

const NAV_ITEMS = [
  { label: "Dashboard",  href: "/",          icon: LayoutDashboard },
  { label: "Pedidos",    href: "/pedidos",    icon: ShoppingBag     },
  { label: "Gastos",     href: "/gastos",     icon: Receipt         },
  { label: "Calendario", href: "/calendario", icon: CalendarDays    },
  { label: "Reportes",   href: "/reportes",   icon: BarChart3       },
  { label: "Exportar",   href: "/exportar",   icon: Download        },
  { label: "Pedidos en línea", href: "/configuracion-pedidos", icon: Settings },
] as const;

interface AppShellProps { children: ReactNode }

export function AppShell({ children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout }  = useAuth();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  return (
    <div className="flex min-h-screen bg-background">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/30 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-30 flex flex-col w-[var(--sidebar-width)]",
        "bg-white border-r border-[#DEDCD5] transition-transform duration-200",
        "lg:translate-x-0 lg:static lg:z-auto",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-4 h-[var(--header-height)] border-b border-[#DEDCD5] shrink-0">
          <img src="/logo.png" alt="Cold Crew Manager" className="size-8 shrink-0 object-contain" />
          <span className="font-display text-[13.5px] font-extrabold leading-tight tracking-tight text-[#111111]">
            COLD CREW MANAGER
          </span>
          <button className="ml-auto lg:hidden text-black/40 hover:text-black" onClick={() => setSidebarOpen(false)}>
            <X className="size-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-1">
         {/* Ítems de navegación — activo usa el mismo degradado del Hero del Dashboard */}
          {NAV_ITEMS.map(({ label, href, icon: Icon }) => {
            const isActive = href === "/" ? currentPath === "/" : currentPath.startsWith(href);
            return (
              <Link key={href} to={href} onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150",
                  isActive
                    ? "bg-[#090909] text-white font-semibold shadow-sm"
                    : "text-[#575550] font-medium hover:bg-[#FFF0E5] hover:text-[#090909]"
                )}>
                <Icon className={cn("size-[18px] shrink-0", isActive && "text-[#FF6500]")} strokeWidth={2} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Logout + versión */}
        <div className="shrink-0 p-3 border-t border-[#DEDCD5] space-y-2">
          <button onClick={logout}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#575550] hover:bg-[#FFF0E5] hover:text-[#090909] transition-colors">
            <LogOut className="size-[18px] shrink-0" strokeWidth={2} />
            Cerrar sesión
          </button>
          <p className="text-center text-[11px] text-neutral-400">Versión 1.0</p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile top bar */}
        <header className="sticky top-0 z-10 flex items-center h-[var(--header-height)] px-4 border-b border-border bg-background/80 backdrop-blur-md shrink-0 lg:hidden">
          <button className="p-1.5 -ml-1.5 text-foreground/70 hover:text-foreground" onClick={() => setSidebarOpen(true)}>
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-2 ml-2">
            <img src="/logo.png" alt="" className="size-6 object-contain" />
            <span className="font-display text-sm font-extrabold tracking-tight">COLD CREW MANAGER</span>
          </div>
        </header>

        {/* Content area — marca de agua global */}
        <main className="flex-1 overflow-auto scrollbar-thin relative">
          {/* Logo sin fondo como marca de agua detrás de todos los módulos */}
          <img
            src="/logo_sin_fondo.png"
            alt=""
            aria-hidden
            className="pointer-events-none select-none fixed top-1/2 left-[calc(50%+122px)] -translate-x-1/2 -translate-y-1/2 w-[45vw] max-w-[480px] opacity-[0.05] z-0 hidden lg:block"
          />
          {/* En móvil, centrado normal */}
          <img
            src="/logo_sin_fondo.png"
            alt=""
            aria-hidden
            className="pointer-events-none select-none fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[340px] opacity-[0.05] z-0 lg:hidden"
          />
          <div className="relative z-10 h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

